// TRAC-1046 — the one door the QA extension uses to clear this embed.
//
// Why a window hook and not a postMessage from the parent. The embed is a
// CROSS-ORIGIN iframe inside tirerack.com, so the same-origin policy is the gate:
// nothing on the parent page can reach this hook, and only an extension holding
// host permissions for the embed's origin (or our own app code) can call it. A
// postMessage channel would hand every script on a tirerack.com page the ability
// to wipe a real customer's conversation, which is not a trade worth making for
// a QA convenience.
//
// Why it is async. The caller is about to DESTROY this frame — the extension's
// New session tears the widget down and re-injects it. `handleClearSession` fires
// the end-handoff POST and the session DELETE without awaiting them (nothing in
// the customer's own flow needs to wait), and a detached frame's in-flight fetch
// is cancelled. That cancellation is exactly what would leave a LivePerson
// conversation ringing with nobody on our end, so the hook holds the caller until
// those requests have settled.

/** The name the extension's service worker looks for. Changing it is a breaking change. */
export const EMBED_CLEAR_HOOK = '__TRACI_EMBED_CLEAR';

export interface EmbedClearResult {
  /** True when the clear itself ran. The settle wait failing does not make it false. */
  cleared: boolean;
  /** Present only when the clear threw. */
  error?: string;
}

type Hook = () => Promise<EmbedClearResult>;

/**
 * Put the clear hook on `target` (the embed's `window`) and hand back the
 * uninstaller.
 *
 * @param clear  the real `handleClearSession` — `resetHandoff()` first, then the
 *               session id rolls over. Deliberately not re-implemented here: the
 *               order is the whole point, and a copy would drift.
 * @param settle resolves once the clear's own in-flight requests have finished.
 */
export function installEmbedClearBridge(
  target: Record<string, unknown>,
  clear: () => void,
  settle: () => Promise<void>,
): () => void {
  const hook: Hook = async () => {
    try {
      clear();
    } catch (e) {
      // Never throw at the extension: it awaits this before tearing the frame
      // down, and an unhandled rejection there strands New session mid-way.
      return { cleared: false, error: e instanceof Error ? e.message : String(e) };
    }
    // The clear ran. A failed wait is worth nothing to the caller beyond that.
    try {
      await settle();
    } catch { /* noop */ }
    return { cleared: true };
  };

  target[EMBED_CLEAR_HOOK] = hook;

  return () => {
    // React can unmount an old page after a newer one has mounted and installed
    // its own hook. Only ever remove our own.
    if (target[EMBED_CLEAR_HOOK] === hook) delete target[EMBED_CLEAR_HOOK];
  };
}
